// Função principal para aplicar data-test em tabelas
function applyTestIdToTable(
  root: HTMLElement,
  currentTestId: string,
  attribute: string,
  customId?: (ctx: {
    tag: string;
    name?: string;
    index: number;
    parentName?: string;
  }) => string,
  parentName?: string,
) {
  const tables =
    root.tagName === 'TABLE' ? [root] : Array.from(root.querySelectorAll('table'));
  tables.forEach((table: HTMLElement) => {
    const thead = table.querySelector('thead');
    const tbody = table.querySelector('tbody');
    if (thead) {
      const ths = thead.querySelectorAll('th');
      ths.forEach((th: HTMLElement, idx: number) => {
        const thText = th.textContent || th.innerText || '';
        const id = customId
          ? customId({ tag: 'th', name: thText, index: idx + 1, parentName })
          : `${currentTestId}-column-${toKebabCase(thText.toLowerCase())}`;
        th.setAttribute(attribute, id);
      });
    }
    if (tbody) {
      const trs = tbody.querySelectorAll('tr');
      trs.forEach((tr: HTMLElement, i: number) => {
        const dtRow = customId
          ? customId({ tag: 'tr', index: i + 1, parentName })
          : `${currentTestId}-row-${i + 1}`;
        tr.setAttribute(attribute, dtRow);
        Array.from(tr.children).forEach((td, j) => {
          const tdElem = td as HTMLElement;
          const dtCell = customId
            ? customId({ tag: 'td', index: j + 1, parentName })
            : `${dtRow}-column-${j + 1}`;
          tdElem.setAttribute(attribute, dtCell);
          // Inputs
          const inputs = tdElem.querySelectorAll('input');
          inputs.forEach((input, idx) => {
            input.setAttribute(
              attribute,
              customId
                ? customId({ tag: 'input', index: idx + 1, parentName })
                : `${dtCell}-input-${idx + 1}`,
            );
          });
          // Botões
          const buttons = tdElem.querySelectorAll('button');
          buttons.forEach((button, idx) => {
            button.setAttribute(
              attribute,
              customId
                ? customId({ tag: 'button', index: idx + 1, parentName })
                : `${dtCell}-button-${idx + 1}`,
            );
          });
        });
      });
    }
  });
}

// Função principal para aplicar data-test em listas (ul/ol) e seus itens (li)
function applyTestIdToLists(
  root: HTMLElement,
  baseTestId: string,
  attribute: string,
  customId?: (ctx: {
    tag: string;
    name?: string;
    index: number;
    parentName?: string;
  }) => string,
  parentName?: string,
  startIndex = 1,
) {
  function applyToLis(
    listRoot: HTMLElement,
    baseId: string,
    parent: string,
    idx = 1,
  ): number {
    const lis = listRoot.querySelectorAll(':scope > li');
    let visibleIndex = idx;
    for (let i = 0; i < lis.length; i++) {
      const li = lis[i];
      if (!(li instanceof HTMLElement)) continue;
      if (li.tagName !== 'LI') continue;
      const liTestId = customId
        ? customId({ tag: 'li', index: visibleIndex, parentName: parent })
        : `${baseId}-item-${visibleIndex}`;
      li.setAttribute(attribute, liTestId);
      // Inputs e botões dentro do <li>
      const inputs = li.querySelectorAll('input');
      inputs.forEach((input: Element, j: number) => {
        (input as HTMLElement).setAttribute(
          attribute,
          customId
            ? customId({ tag: 'input', index: j + 1, parentName: parent })
            : `${liTestId}-input-${j + 1}`,
        );
      });
      const buttons = li.querySelectorAll('button');
      buttons.forEach((button: Element, j: number) => {
        (button as HTMLElement).setAttribute(
          attribute,
          customId
            ? customId({ tag: 'button', index: j + 1, parentName: parent })
            : `${liTestId}-button-${j + 1}`,
        );
      });
      // Recursão para listas aninhadas dentro deste <li>
      const sublists = li.querySelectorAll(':scope > ul, :scope > ol');
      sublists.forEach((sublist) => {
        if (sublist instanceof HTMLElement) {
          applyToLis(sublist, liTestId, parent, 1);
        }
      });
      visibleIndex++;
    }
    return visibleIndex;
  }
  const lists =
    root.tagName === 'UL' || root.tagName === 'OL'
      ? [root]
      : Array.from(root.querySelectorAll('ul, ol'));
  lists.forEach((list: Element) => {
    if (list instanceof HTMLElement) {
      applyToLis(list, baseTestId, parentName || '', startIndex);
    }
  });
}

// Interface de configuração avançada
export interface VueTestIdPluginOptions {
  attribute?: string; // nome do atributo, ex: 'data-test', 'data-testid'
  prefix?: string; // prefixo customizável
  overwrite?: boolean; // sobrescrever atributos existentes
  targets?: string[]; // tags alvo, ex: ['input', 'button']
  customId?: (ctx: {
    tag: string;
    name?: string;
    index: number;
    parentName?: string;
  }) => string; // função customizada
  testAttributeName?: string;
  useLocation?: boolean;
  override?: boolean;
}

import { nextTick, watchPostEffect, getCurrentInstance, onMounted } from 'vue';
import {
  isEmptyString,
  hasTransition,
  toKebabCase,
  isDefaultSkipping,
  findValidParentName,
  mergeTestIdKey,
} from './helpers.js';
import type { VueTestIdPluginOptions as VueTestIdPluginOptionsType } from './types.js';

// Aplica data-test em elementos interativos (inputs, buttons, selects, etc)
function applyTestIdToInteractiveElements(
  el: HTMLElement,
  currentTestId: string,
  attribute: string,
  targets: string[],
  customId?: (ctx: {
    tag: string;
    name?: string;
    index: number;
    parentName?: string;
  }) => string,
  parentName?: string,
) {
  if (!el) return;
  targets.forEach((tag) => {
    const elements = el.querySelectorAll(tag);
    elements.forEach((element: Element, idx: number) => {
      const name = (element as HTMLElement).getAttribute?.('name') || undefined;
      const id = customId
        ? customId({ tag, name, index: idx + 1, parentName })
        : `${currentTestId}-${tag}-${idx + 1}`;
      (element as HTMLElement).setAttribute(attribute, id);
    });
  });
}

// Observa mutações no DOM e reaplica data-test
function observeDomMutations(el: HTMLElement, callback: () => void) {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
  const observer = new MutationObserver(() => {
    callback();
  });
  observer.observe(el, { childList: true, subtree: true });
}

// Composable principal para Composition API
export function useTestId(elRef: any, options: VueTestIdPluginOptionsType = {}) {
  // SSR-safe: só roda no client
  if (typeof window === 'undefined') return;
  const attribute = options.attribute || options.testAttributeName || 'data-test';
  const overwrite =
    typeof options.overwrite === 'boolean'
      ? options.overwrite
      : typeof options.override === 'boolean'
        ? options.override
        : true;
  const prefix = options.prefix || '';
  const targets = options.targets || ['input', 'button', 'select', 'textarea'];
  const customId = options.customId;
  const useLocation =
    typeof options.useLocation === 'boolean' ? options.useLocation : true;

  function findFirstRealElement(node: any): HTMLElement | null {
    if (!node) return null;
    if (node.$el) return findFirstRealElement(node.$el);
    if (Array.isArray(node)) {
      for (const n of node) {
        const found = findFirstRealElement(n);
        if (found) return found;
      }
      return null;
    }
    if (
      node.nodeType === 1 &&
      (node.id === 'app' ||
        node.hasAttribute?.('data-v-app') ||
        node.getAttribute?.('role') === 'presentation' ||
        node.className?.includes('v-application')) &&
      node.childNodes &&
      node.childNodes.length === 1
    ) {
      return findFirstRealElement(node.childNodes[0]);
    }
    if (node.nodeType === 1) return node;
    return null;
  }

  function setDataTestComposition() {
    const rootEl = findFirstRealElement(elRef && elRef.value ? elRef.value : elRef);
    if (!rootEl) return;

    const instance = getCurrentInstance();
    const instanceName = instance?.type?.name || instance?.type?.__name;
    if (typeof instanceName !== 'string' || instanceName === '') return;
    if (hasTransition(instanceName)) return;
    if (isDefaultSkipping(instanceName)) return;

    let testIdKey = toKebabCase(instanceName);
    if (prefix) testIdKey = `${prefix}-${testIdKey}`;
    let locationPrefix = '';
    if (useLocation && typeof window !== 'undefined' && window.location) {
      const path = window.location.pathname || '';
      const splittedLocation = (path || '').split('/');
      if (splittedLocation.every((pathName: string) => isEmptyString(pathName))) {
        locationPrefix = 'root-';
      } else {
        locationPrefix =
          splittedLocation.at(-1) !== splittedLocation.at(1)
            ? `${splittedLocation.at(1)?.toLocaleLowerCase()}-${splittedLocation
                .at(-1)
                ?.toLocaleLowerCase()}-`
            : `${splittedLocation.at(1)?.toLocaleLowerCase()}-`;
      }
    }
    testIdKey = `${locationPrefix}${testIdKey}`;

    let finalTestIdKey = testIdKey;
    if (rootEl.getAttribute) {
      const dataName = rootEl.getAttribute('data-name');
      if (dataName) {
        finalTestIdKey = `${finalTestIdKey}-${toKebabCase(dataName)}`;
      }
    }
    const originalTestId = rootEl.getAttribute
      ? rootEl.getAttribute(attribute)
      : undefined;
    if ((!originalTestId || overwrite) && rootEl.setAttribute) {
      rootEl.setAttribute(attribute, finalTestIdKey);
    }
    const currentTestId = rootEl.getAttribute
      ? rootEl.getAttribute(attribute)
      : undefined;
    if (currentTestId) {
      applyTestIdToInteractiveElements(
        rootEl,
        currentTestId,
        attribute,
        targets,
        customId,
        undefined,
      );
      applyTestIdToTable(rootEl, currentTestId, attribute, customId, undefined);
      const safeInstanceName = typeof instanceName === 'string' ? instanceName : '';
      const componentName = toKebabCase(safeInstanceName);
      const componentTestId = prefix
        ? `${locationPrefix}${prefix}-${componentName}`
        : `${locationPrefix}${componentName}`;
      applyTestIdToLists(rootEl, componentTestId, attribute, customId, componentName, 1);
    }
  }

  onMounted(() => {
    setDataTestComposition();
    nextTick(() => setDataTestComposition());
    watchPostEffect(() => {
      setDataTestComposition();
    });
    if (elRef.value || elRef) {
      observeDomMutations(elRef.value || elRef, setDataTestComposition);
    }
  });
}

// Instalação global: plugin para Vue 3
const VueTestIdPlugin = {
  install(app: any, options: VueTestIdPluginOptionsType = {}) {
    // API global para uso em Options API
    app.mixin({
      mounted() {
        try {
          if (typeof window === 'undefined') return;
          // Só aplica se existir $el e $options
          if (!this.$el || !this.$options) return;
          const instanceName = this.$options?.name || this.$options?.__name;
          if (typeof instanceName !== 'string' || instanceName === '') return;
          if (hasTransition(instanceName)) return;
          if (isDefaultSkipping(instanceName)) return;
          let testIdKey = toKebabCase(instanceName);
          // Se tiver parent válido, compõe o testid
          const parentName = findValidParentName(this);
          if (parentName) testIdKey = mergeTestIdKey(parentName, instanceName);
          // Location
          let locationPrefix = '';
          if (
            options.useLocation !== false &&
            typeof window !== 'undefined' &&
            window.location
          ) {
            const path = window.location.pathname || '';
            const splittedLocation = (path || '').split('/');
            if (splittedLocation.every((pathName: string) => isEmptyString(pathName))) {
              locationPrefix = 'root-';
            } else {
              locationPrefix =
                splittedLocation.at(-1) !== splittedLocation.at(1)
                  ? `${splittedLocation.at(1)?.toLocaleLowerCase()}-${splittedLocation
                      .at(-1)
                      ?.toLocaleLowerCase()}-`
                  : `${splittedLocation.at(1)?.toLocaleLowerCase()}-`;
            }
          }
          testIdKey = `${locationPrefix}${testIdKey}`;
          // Se tiver data-name
          if (this.$el.getAttribute) {
            const dataName = this.$el.getAttribute('data-name');
            if (dataName) {
              testIdKey = `${testIdKey}-${toKebabCase(dataName)}`;
            }
          }
          const attribute = options.attribute || options.testAttributeName || 'data-test';
          const overwrite =
            typeof options.overwrite === 'boolean'
              ? options.overwrite
              : typeof options.override === 'boolean'
                ? options.override
                : true;
          const originalTestId = this.$el.getAttribute(attribute);
          if ((!originalTestId || overwrite) && this.$el.setAttribute) {
            this.$el.setAttribute(attribute, testIdKey);
          }
          const currentTestId = this.$el.getAttribute(attribute);
          if (currentTestId) {
            const targets = options.targets || ['input', 'button', 'select', 'textarea'];
            const customId = options.customId;
            applyTestIdToInteractiveElements(
              this.$el,
              currentTestId,
              attribute,
              targets,
              customId,
              undefined,
            );
            applyTestIdToTable(this.$el, currentTestId, attribute, customId, undefined);
            const componentName = toKebabCase(instanceName);
            const componentTestId = options.prefix
              ? `${locationPrefix}${options.prefix}-${componentName}`
              : `${locationPrefix}${componentName}`;
            applyTestIdToLists(
              this.$el,
              componentTestId,
              attribute,
              customId,
              componentName,
              1,
            );
          }
        } catch (error: any) {
          if (error.message.includes('this.$el.getAttribute is not a function'))
            return null;
          console.error('Error in VueTestIdPlugin:', error);
        }
      },
    });
    // Expor composable para Composition API
    app.config.globalProperties.$useTestId = (elRef: any, opts = {}) =>
      useTestId(elRef, { ...options, ...opts });
  },
};

export default VueTestIdPlugin;
